import { useCallback, useEffect, useRef, useState } from 'react'

import styled, { css } from 'styled-components'

import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import useChatbot from '@hooks/useChatbot'
import { ArrowsVisibilityType, SwiperClass } from '@hooks/useSwiper'
import useViewportType from '@hooks/useViewport/useViewportType'
import Blink from '@components/Blink'
import ResponsiveImageV2 from '@components/ResponsiveImageV2'
import SummaryPill from '@components/Summary/SummaryPill'
import SvgIcon from '@components/SvgIcon'
import Swiper from '@components/Swiper'
import NavigationArrows from '@components/Swiper/NavigationArrows'

import { ChatbotArticleSliderProps } from './types'

export const SliderWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing16, spacing8 },
    },
  }) => css`
    position: relative;
    margin-top: ${spacing8};
    margin-bottom: ${spacing16};
  `}
`

export const StyledSwiper = styled(Swiper)`
  ${({
    theme: {
      spacing: { spacing16, spacing24 },
    },
  }) => css`
    width: auto;
    height: auto;
    overflow: visible;

    margin: 0;
    margin-right: -${spacing16};

    & > div {
      gap: ${spacing16};
    }

    ${desktopCSS(css`
      margin-right: -${spacing16};
    `)}

    > .swiper-items-container {
      scroll-snap-type: none; // Important otherwise the gap between the items is not respected
    }

    > .swiper-items-container > *:first-child {
      scroll-margin-left: ${spacing16};

      ${desktopCSS(css`
        scroll-margin-left: ${spacing24};
      `)}
    }

    > .swiper-items-container > *:last-child {
      margin-right: ${spacing16};
      scroll-margin-right: ${spacing16};

      ${desktopCSS(css`
        margin-right: ${spacing24};
        scroll-margin-right: ${spacing24};
      `)}
    }
  `}
`
export const StyledSwiperHeader = styled.div`
  ${({
    theme: {
      spacing: { spacing8, spacing16 },
    },
  }) => css`
    margin-top: ${spacing16};
    margin-bottom: ${spacing8};
    display: flex;
    justify-content: space-between;
    flex-wrap: nowrap;
  `}
`

export const StyledSwiperHeadline = styled.h1`
  ${({
    theme: {
      typography: {
        headings: {
          small: { bundledCSS: smallHeadings },
        },
      },
    },
  }) => css`
    display: flex;
    align-items: center;
    margin: 0;
    ${smallHeadings};
    text-transform: uppercase;
  `}
`

export const StyledSwiperText = styled.div`
  ${({
    theme: {
      typography: {
        bodycopy: {
          xxsmall: { bundledCSS: xxsmallBodyCopy },
        },
      },
    },
  }) => css`
    ${xxsmallBodyCopy}
  `}
`

export const StyledNavigationArrows = styled(NavigationArrows)<{
  itemsCount: number
}>`
  ${({ itemsCount }) => css`
    display: flex;
    align-items: center;

    ${itemsCount <= 1 &&
    mobileCSS(css`
      display: none;
    `)};
    ${itemsCount <= 2 &&
    tabletCSS(css`
      display: none;
    `)};
    ${itemsCount <= 4 &&
    desktopCSS(css`
      display: none;
    `)}
  `}
`

export const SwiperSlide = styled(Blink)`
  ${({
    theme: {
      spacing: { spacing8 },
      typography: {
        bodycopy: {
          xxsmall: { bundledCSS: xxsmallBodyCopy },
        },
      },
    },
  }) => css`
    display: flex;
    gap: ${spacing8};
    flex-direction: column;
    justify-content: space-between;
    text-decoration: none;
    box-sizing: border-box;
    width: 160px;
    height: auto;
    border-radius: 4px;
    padding: ${spacing8};

    ${xxsmallBodyCopy}

    &:hover {
      text-decoration: none;
    }
  `}
`

const StyledPlusIcon = styled(SvgIcon)`
  ${({
    theme: {
      spacing: { spacing8 },
    },
  }) => {
    return css`
      vertical-align: text-bottom;
      margin-right: ${spacing8};
    `
  }}
`

const StyledSwiperSlideContent = styled.div`
  ${({
    theme: {
      spacing: { spacing8 },
      colors: { staticBlack },
      utils: { opacity },
    },
  }) => css`
    width: 100%;
    height: 90px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: ${spacing8};
    gap: ${spacing8};
    background: linear-gradient(
      184.15deg,
      ${opacity(staticBlack, '00')} 12.29%,
      ${opacity(staticBlack, '99')} 49.76%
    );
    max-height: 90px;
    box-sizing: border-box;
    position: relative;
    z-index: 1;
  `}
`
const StyledSwiperSlideText = styled.span`
  display: -webkit-box;
  max-width: 200px;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const StyledSummaryPill = styled(SummaryPill)`
  ${({
    theme: {
      colors: { staticWhite },
      typography: {
        labels: { label3 },
      },
    },
  }) => css`
    background: ${staticWhite} !important;
    background-color: ${staticWhite};
    ${label3};
    width: 16px;
    height: 16px;
    & span {
      background: ${staticWhite};
      background-color: ${staticWhite};
      ${label3}
      width: 16px;
      height: 16px;
    }
  `}
`

const StyledImageWrapper = styled.div`
  ${({}) => css`
    position: absolute;
    top: 0;
    left: 0;
    width: 168px;
    height: 112px;
  `}
`

const StyledSwiperSlide = styled(SwiperSlide)<{ showPill: boolean }>`
  ${({
    showPill,
    theme: {
      colors: { staticWhite, staticBlack },
      typography: {
        headings: {
          xxsmall: { bundledCSS: xxsmallHeadings },
        },
        labels: {
          label3: { bundledCSS: label3Labels },
        },
      },
    },
  }) => css`
    width: 168px;
    height: 112px;

    color: ${staticWhite};

    ${xxsmallHeadings};
    border: none;
    padding: 0;
    display: flex;
    justify-content: flex-end;
    position: relative;
    ${showPill &&
    `span:first-child {
      background-color: ${staticWhite};
      color: ${staticBlack};
      width: 16px;
      height: 16px;
      ${label3Labels}
    }`}

    &:hover {
      text-decoration: none;
      border-color: transparent;
    }
  `}
`

const ChatbotArticleSlider = ({
  links,
  activeIndex,
  showPill = true,
  title,
  onSlideClick,
}: ChatbotArticleSliderProps) => {
  const { SCROLL_CONTAINER_CLASS_NAME } = useChatbot()
  const swiperHeaderRef = useRef<HTMLDivElement>(null)
  const swiperRef = useRef<SwiperClass | null>(null)
  const [arrowsVisibility, setArrowsVisibility] =
    useState<ArrowsVisibilityType>('none')
  const linksCount = Object.keys(links ?? {}).length
  const viewportType = useViewportType()
  const isDesktop = viewportType === 'desktop'

  const onInit = useCallback(
    (swiper: SwiperClass) => {
      if (swiperRef) {
        swiperRef.current = swiper
      }
    },
    [swiperRef],
  )

  const onArrowsVisibility = useCallback((arrow: ArrowsVisibilityType) => {
    setArrowsVisibility(arrow)
  }, [])

  useEffect(() => {
    if (swiperHeaderRef.current && activeIndex !== null && activeIndex >= 0) {
      const container = document.querySelector(
        `.${SCROLL_CONTAINER_CLASS_NAME}`,
      )
      const scrollTop = container?.scrollTop || 0
      const top =
        swiperHeaderRef.current?.getBoundingClientRect().top + scrollTop - 200

      container?.scrollTo({ top, behavior: 'smooth' })
      swiperRef.current?.slideTo(activeIndex)
    }
  }, [activeIndex, SCROLL_CONTAINER_CLASS_NAME])

  return (
    <>
      <StyledSwiperHeader ref={swiperHeaderRef}>
        <StyledSwiperHeadline>{title}</StyledSwiperHeadline>

        {arrowsVisibility !== 'none' && (
          <StyledNavigationArrows
            swiperRef={swiperRef}
            arrowsVisibility={arrowsVisibility}
            itemsCount={linksCount}
          />
        )}
      </StyledSwiperHeader>
      <StyledSwiper
        initialSlide={0}
        spaceTopBottom={0}
        slidesPerGroup={isDesktop ? 4 : 2}
        spaceBetween={8}
        onInit={onInit}
        onArrowsVisibility={onArrowsVisibility}
        slides={Object.keys(links).map((key) => {
          const item = links && links[key]
          const url = new URL(item.url)
          return (
            <StyledSwiperSlide
              href={url.pathname}
              key={`source-item-${item.url}-${key}`}
              onClick={() => onSlideClick?.(item.url)}
              showPill={showPill}
            >
              <StyledSwiperSlideContent>
                {showPill && (
                  <StyledSummaryPill
                    isActive={activeIndex === parseInt(key) - 1}
                  >
                    {key}
                  </StyledSummaryPill>
                )}
                <StyledSwiperSlideText>
                  {item.isPlus && (
                    <StyledPlusIcon iconName="blick-plus-logo" size={19} />
                  )}
                  {item.title}
                </StyledSwiperSlideText>
              </StyledSwiperSlideContent>
              <StyledImageWrapper>
                {item.image && (
                  <ResponsiveImageV2
                    alt={item.image.alt}
                    src={item.image.src}
                    crops={item.image.crops}
                    widths={{ desktop: 168, tablet: 168, mobile: 168 }}
                    ratios={{
                      desktop: '3_2',
                      tablet: '3_2',
                      mobile: '3_2',
                    }}
                  />
                )}
              </StyledImageWrapper>
            </StyledSwiperSlide>
          )
        })}
      />
    </>
  )
}

export default ChatbotArticleSlider
